import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Breadcrumb, ButtonWithIcon, Icon, ProgressIndicator } from '@/components/atoms'
import { FileUploadWithModal, PageContainer } from '@/components/organisms'
import JsonFormsWrapper from '@/components/common/jsonForms/jsonFormsWrapper/JsonFormsWrapper'
import { useCreateBulkPointModifiers } from '@/pages/BulkPointModifiers/CreateBulkPointModifiers-hook'
import { PageHeader, SaveCancelFooter, SectionTitle } from '@/components/molecules'
import { ROUTES } from '@/utils/constants/routesConstants'
import { SIZE } from '@/utils/constants/globalStyle'
import { COLOR } from '@/utils/constants/colors'
import {getModal} from '@/utils/CommonModal'
import './_createBulkPointModifiers.scss'
import { useAppDispatch } from '@/store'
import { setBulkModifiers } from '@/store/slices/bulkPointSlice'
import { setToastNotifications } from '@/store/slices/commonSlice'
import { ACTION_DROPDOWN, Constants, STORE_SELECTOR_DROPDOWN } from '@/utils/constants/commonConstants'
import { convertToLowerCase } from '@/utils/commons'
import { setStoreListUrl } from '@/store/slices/salesTargetSlice'
import useReviewBulkPointModifier from '@/containers/ReviewBulkPointModifiers/ReviewBulkPointModifier-hooks'

export const CreateBulkPointModifiers = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [isCancel, setIsCancel] = useState(false);

  const [openUpload, setOpenUpload] = useState(false)
  const {
    formData,
    setFormData,
    handleFormChange,
    handleFileDownload,
    validateFormData,
    getScheduleInfo,
    bulkModifierSetupJsonSchema,
    bulkModifierSetupUISchema,
    rulesJsonSchema,
    rulesUISchema,
    numOfDaysSchema,
    numOfDaysUISchema,
    numOfPointsSchema,
    numOfPointsUISchema,
    scheduleJsonSchema,
    scheduleUISchema,
    startDateJsonSchema,
    isNameValid,
    setIsNameValid,
    handleValidationModalClose,
  } = useCreateBulkPointModifiers()
  const { handleDraft,handleNameValidation } = useReviewBulkPointModifier()
  const handleSaveDraft = async () => {
   const data =  await validateName()
   if(data){
    handleDraft()
    }
  }
  useEffect(() => {
    dispatch(setBulkModifiers(formData));
  }, [dispatch, formData]);
  const validateName = async () => {
    const bulkModifierName = formData?.bulkModifierName

    // Check if name is empty or whitespace only
    if (!bulkModifierName || String(bulkModifierName).trim() === '') {
      dispatch(
        setToastNotifications({
          show: true,
          message: 'Bulk Modifier Name is mandatory',
          type: 'error',
        })
      )
      return false
    }

      const params = {
        name: bulkModifierName.trim(),
        id: formData?.id ?? null,
        uuid: formData?.uuid ?? null
      }
      const resp = await handleNameValidation(params)
      const isValid = resp?.success === true
      setIsNameValid(isValid)
      return isValid
  }  

  const handleNext = async () => {
    const isValidName = await validateName()
    if (!isValidName) return
    dispatch(setBulkModifiers(formData))
    navigate(ROUTES.BULK_POINT_MODIFIER_REVIEW)
  }

  const handleCancel = () => {
    setIsCancel(!isCancel);
  }
  const onClickPrimaryButton = () => {
    navigate(ROUTES.BULK_POINT_MODIFIERS)
  }

  const onClickSecondaryButton = () => {
    setIsCancel(!isCancel)
  }
  const isInValidData = validateFormData({ ...formData })
  const { scheduleRowClass, showStartDate } = getScheduleInfo(formData?.schedule?.toLowerCase())

  /* Rendering startDate ui schema for dynamic validation */
  const renderStartDate = (startDate, endDate) => ({
    type: 'HorizontalLayout',
    className: '',
    elements: [
      {
        type: 'HorizontalLayout',
        elements: [
          {
            type: 'Control',
            scope: '#/properties/startDate',
            component: 'datepicker',
            label: 'Start Date*',
            placeholder: '',
            isDark: true,
            required: false,
            dependentDate: endDate,
            validationRules: [
              {
                type: 'required',
                msg: ' is required',
              },
              {
                type: 'startDate',
                msg: ' cannot be in the past.',
              },
              {
                type: 'startDate',
                endDate: endDate,
              },
            ],
          },
        ],
      },
    ],
  })
  const getCustomModal = (type) => {
    switch(type) {
      case "CANCEL_MODAL":
        return getModal({
          title: "Are you sure you want to cancel?",
          text: 'Your work will not be saved if you click Yes. To continue editing and go back to the page, click NO',
          showPrimaryButton: true, 
          primaryButtonText: "Yes",
          onClickPrimaryButton: onClickPrimaryButton, 
          showSecondaryButton:   true,
          secondaryButtonText: "No",
          onClickSecondaryButton: onClickSecondaryButton, 
          showModal: isCancel, 
          onCloseModal: handleCancel, 
          hideCloseButton: false
        })
      case "NAME_VALIDATION_MODAL":
        return getModal({
          title: "Duplicate Name Detected",
          text: (
            <>
              It looks like there's already a Bulk Point Modifier with this name.<br />
              To avoid confusion, please enter a unique name before continuing.
            </>
              ),
          showPrimaryButton: true, 
          primaryButtonText: "Edit Name",
          onClickPrimaryButton: handleValidationModalClose, 
          showSecondaryButton:   false,
          showModal: !isNameValid, 
          onCloseModal: handleValidationModalClose, 
          hideCloseButton: true
        })
      default:
        return null;
    }
  }

  return (
    <div className="flex flex-col">
      <Breadcrumb
        uiSchema={{
          isDark: true,
          className: '',
          breadcrumbs: [
            {
              label: '',
              href: '/',
            },
            {
              label: 'Bulk Point Modifiers',
              href: '/rewards/bulk-modifiers',
            },
            {
              label: 'Create Bulk Modifiers',
              href: '/rewards/bulk-modifiers',
            },
          ],
        }}
        id={'cbpm_bradcrumb'}
      />
      <PageHeader title="Create Bulk Point Modifiers" />
      <ProgressIndicator
        currentStep={1}
        completed={false}
        steps={2}
        titles={[{title: 'Setup'}, {title: 'Review'}]}
        onStepClick={step => {
          if (step === 2) {
            navigate(ROUTES.BULK_POINT_MODIFIER_REVIEW)
          }
        }}
      />
      <PageContainer data-testid="create-bulk-point-modifiers-content">
        <div className="createBulkPointModifiersContent">
          <div className="w-1/2">
            <SectionTitle label="Bulk Modifier Setup" />
            <div className="flex flex-col gap-4 lg:flex-row">
              <JsonFormsWrapper
                jsonschema={bulkModifierSetupJsonSchema}
                uischema={bulkModifierSetupUISchema(formData)}
                data={formData}
                onChange={data => handleFormChange(data)}
              />
            </div>
          </div>

          <div className="w-1/2">
            <SectionTitle label="Rules" />
            <div className="flex flex-col gap-4 lg:flex-row">
              <JsonFormsWrapper
                jsonschema={rulesJsonSchema}
                uischema={rulesUISchema}
                data={formData}
                onChange={data => {
                  data.numberOfDays = ''
                  data.numberOfPoints = ''
                  handleFormChange(data)
                }}
              />
              {convertToLowerCase(formData?.actions) === ACTION_DROPDOWN.REMOVE_ALL_EXCEPT && (
                <JsonFormsWrapper
                  jsonschema={numOfDaysSchema}
                  uischema={numOfDaysUISchema}
                  data={formData}
                  onChange={data => handleFormChange(data)}
                />
              )}
              {convertToLowerCase(formData?.actions) === ACTION_DROPDOWN.REMOVE_NUM_POINTS && (
                <JsonFormsWrapper
                  jsonschema={numOfPointsSchema}
                  uischema={numOfPointsUISchema}
                  data={formData}
                  onChange={data => handleFormChange(data)}
                />
              )}
              {convertToLowerCase(formData?.actions) === ACTION_DROPDOWN.ADD_POINTS && (
                <JsonFormsWrapper
                  jsonschema={numOfPointsSchema}
                  uischema={numOfPointsUISchema}
                  data={formData}
                  onChange={data => handleFormChange(data)}
                />
              )}
            </div>
          </div>

          <div className={scheduleRowClass}>
            <SectionTitle label="Availability" />
            <div className="flex flex-col gap-4 lg:flex-row">
              <JsonFormsWrapper
                jsonschema={scheduleJsonSchema}
                uischema={scheduleUISchema}
                data={formData}
                onChange={data => {
                  data.startDate = ''
                  data.endDate = ''
                  handleFormChange(data)
                }}
              />
              {showStartDate && (
                <JsonFormsWrapper
                  jsonschema={startDateJsonSchema}
                  uischema={renderStartDate(formData?.startDate, formData?.endDate)}
                  data={formData}
                  onChange={data => handleFormChange(data)}
                />
              )}
            </div>
          </div>

          {/* Excel Upload' */}

          {convertToLowerCase(formData?.storeSelector) === STORE_SELECTOR_DROPDOWN.EXCEL_UPLOAD && (
            <div className="">
              <SectionTitle label="Upload File*" />
              <div className="flex flex-col gap-2 lg:flex-row">
                <span className="downLoadIconContainer" onClick={handleFileDownload}>
                  <Icon icon="file_download" size={SIZE.SMALL} isDark={true} />
                </span>
                {formData?.targetMediaFile?.length == 1 ? (
                  <div className="main_upload-title ml-2  flex items-center rounded-lg px-2 py-3.5 text-xs">
                    <Icon alt="file" className="insert_file_icon" icon="insert_drive_file" size="small" />
                    <p className="w-[105px] truncate pl-2">{formData?.targetMediaFile?.[0]}</p>
                    <Icon
                      alt="cached"
                      className="cach_ed_icon"
                      icon="cached"
                      size="small"
                      data-testid="file-delete-icon"
                      onClick={() => {
                        setFormData(prev => ({...prev, targetMediaFile: []}))
                        dispatch(setStoreListUrl(''))
                        setOpenUpload(true)
                      }}
                    />
                  </div>
                ) : (
                  <ButtonWithIcon
                    uiSchema={{
                      placeholder: 'Upload Target Template',
                      title: 'Upload Target Template',
                      size: SIZE.SMALL,
                      variant: COLOR.PRIMARY,
                    }}
                    iconLeading="upload"
                    handleButton={() => setOpenUpload(true)}
                  />
                )}
              </div>
              <FileUploadWithModal
                title="Upload File"
                fileType="csv"
                uploadFor={Constants.BulkModifiers}
                openUpload={openUpload}
                setOpenUpload={setOpenUpload}
                setFormData={setFormData}
                formData={formData}
                customErrorMessages={{
                  wrongFileType: 'Wrong file type, please use CSV file',
                  wrongFormat: 'The format being used is not correct, please use correct file format',
                }}
              />
            </div>
          )}
        </div>
      </PageContainer>
      <SaveCancelFooter
        handleCancel={handleCancel}
        nextAction={handleNext}
        draftAction={handleSaveDraft}
        FormError={isInValidData}
      />
      {!isNameValid && getCustomModal("NAME_VALIDATION_MODAL")}
      {isCancel && getCustomModal("CANCEL_MODAL")}
    </div>
  )
}
